import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend } from 'recharts';
import { primeCategories, scoreToPercent } from '../data/primeReferentiel';

interface SpiderChartProps {
  className?: string;
  mode?: 'skill' | 'will' | 'combined';
}

const chartConfig = {
  skill: {
    title: 'Spider SKILL — Compétence démontrée',
    description: 'Mesure la capacité observable à exécuter la compétence selon l’échelle ITIL 1 à 5.',
    dataKey: 'Skill',
    color: '#2563EB',
  },
  will: {
    title: 'Spider WILL — Volonté & posture',
    description: 'Mesure la constance, l’engagement, la discipline et la posture attendue.',
    dataKey: 'Will',
    color: '#7C3AED',
  },
  combined: {
    title: 'Spider combiné — Moyenne pondérée Skill/Will',
    description: 'Score final par pilier selon la pondération affichée dans le référentiel.',
    dataKey: 'Combiné',
    color: '#10B981',
  },
} as const;

export default function SpiderChart({ className = '', mode = 'combined' }: SpiderChartProps) {
  const config = chartConfig[mode];
  const chartData = primeCategories.map((category) => {
    const skill = Math.round(
      category.subCompetences.reduce((sum, sub) => sum + scoreToPercent(sub.skillScore), 0) /
        category.subCompetences.length,
    );
    const will = Math.round(
      category.subCompetences.reduce((sum, sub) => sum + scoreToPercent(sub.willScore), 0) /
        category.subCompetences.length,
    );
    const combined = scoreToPercent(category.currentScore);

    return {
      category: category.name,
      Skill: skill,
      Will: will,
      Combiné: combined,
      Minimum: scoreToPercent(category.minScore),
    };
  });

  const average = Math.round(
    chartData.reduce((sum, item) => sum + Number(item[config.dataKey]), 0) / chartData.length,
  );

  return (
    <div className={`bg-white rounded-lg p-6 shadow-md ${className}`}>
      <div className="mb-6">
        <h3 className="text-xl font-bold text-bz-dark mb-2">📊 {config.title}</h3>
        <p className="text-gray-600 text-sm mb-4">{config.description}</p>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="border rounded-lg p-3" style={{ backgroundColor: `${config.color}12`, borderColor: `${config.color}55` }}>
            <div className="text-sm font-medium" style={{ color: config.color }}>Score moyen</div>
            <div className="text-2xl font-bold" style={{ color: config.color }}>{average}%</div>
            <div className="text-xs text-gray-500">Lecture universelle en %</div>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <div className="text-sm text-gray-600 font-medium">Échelle ITIL</div>
            <div className="text-2xl font-bold text-gray-800">1 → 5</div>
            <div className="text-xs text-gray-500">1=Initial • 3=Conforme • 5=Référence</div>
          </div>
        </div>
      </div>

      <div className="h-96 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={chartData} margin={{ top: 20, right: 80, bottom: 20, left: 80 }}>
            <PolarGrid stroke="#e5e7eb" />
            <PolarAngleAxis dataKey="category" tick={{ fontSize: 12, fill: '#374151' }} className="text-xs" />
            <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10, fill: '#6b7280' }} tickCount={6} />
            <Radar
              name={config.dataKey}
              dataKey={config.dataKey}
              stroke={config.color}
              fill={config.color}
              fillOpacity={0.12}
              strokeWidth={3}
              dot={{ fill: config.color, strokeWidth: 2, r: 4 }}
            />
            <Radar
              name="Minimum attendu"
              dataKey="Minimum"
              stroke="#E74C3C"
              fill="transparent"
              strokeWidth={1}
              strokeDasharray="3 3"
              dot={false}
            />
            <Legend wrapperStyle={{ paddingTop: '20px' }} iconType="circle" />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-semibold text-gray-800 mb-3">🎯 Piliers forts (≥ 80%)</h4>
          <div className="space-y-1">
            {chartData
              .filter((item) => Number(item[config.dataKey]) >= 80)
              .map((item) => (
                <div key={item.category} className="flex justify-between text-sm">
                  <span className="text-gray-700">{item.category}</span>
                  <span className="font-bold" style={{ color: config.color }}>{Number(item[config.dataKey])}%</span>
                </div>
              ))}
          </div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-semibold text-gray-800 mb-3">📈 Piliers à développer (&lt; 80%)</h4>
          <div className="space-y-1">
            {chartData
              .filter((item) => Number(item[config.dataKey]) < 80)
              .map((item) => (
                <div key={item.category} className="flex justify-between text-sm">
                  <span className="text-gray-700">{item.category}</span>
                  <span className="font-bold text-orange-600">{Number(item[config.dataKey])}%</span>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
